import React, { useState, useEffect } from 'react';
import { X, AlertTriangle, CheckCircle, RefreshCw, Wrench, Shield, Copy, FileText, Users } from 'lucide-react';
import Button from '../../../components/ui/Button';
import { propertyService, communeManagementService } from '../../../lib/supabase';



export default function DataIntegrityCheck({ communes, properties, onClose, onRefresh }) {
  const [issues, setIssues] = useState({
    orphanedProperties: [],
    invalidReferences: [],
    missingData: [],
    duplicates: [],
    pricingAnomalies: []
  });
  
  const [checking, setChecking] = useState(false);
  const [fixing, setFixing] = useState(false);
  const [fixedCount, setFixedCount] = useState(0);
  const [selectedIssue, setSelectedIssue] = useState(null);
  const [showDetails, setShowDetails] = useState(false);
  const [autoFixResults, setAutoFixResults] = useState([]);

  useEffect(() => {
    runIntegrityCheck();
  }, [communes, properties]);

  const runIntegrityCheck = async () => {
    setChecking(true);
    
    try {
      const newIssues = {
        orphanedProperties: [],
        invalidReferences: [],
        missingData: [],
        duplicates: [],
        pricingAnomalies: []
      };

      // Check for orphaned properties (properties without valid commune)
      const communeIds = new Set(communes?.map(c => c?.id));
      properties?.forEach(property => {
        if (property?.commune_id && !communeIds?.has(property?.commune_id)) {
          newIssues?.orphanedProperties?.push({
            id: property?.id,
            title: property?.title,
            commune_id: property?.commune_id,
            type: 'invalid_commune_reference',
            severity: 'high',
            recommendation: 'Assigner à une commune valide ou supprimer la référence'
          });
        }
      });

      // Check for missing essential data
      properties?.forEach(property => {
        const issues = [];
        
        if (!property?.title?.trim()) issues?.push('title');
        if (!property?.address?.trim()) issues?.push('address');
        if (!property?.price || property?.price <= 0) issues?.push('price');
        if (!property?.property_type) issues?.push('property_type');
        if (!property?.description?.trim()) issues?.push('description');
        if (!property?.bedrooms && property?.property_type !== 'land') issues?.push('bedrooms');
        if (!property?.surface_area) issues?.push('surface_area');
        
        if (issues?.length > 0) {
          newIssues?.missingData?.push({
            id: property?.id,
            title: property?.title || 'Sans titre',
            missingFields: issues,
            type: 'missing_essential_data',
            severity: issues?.length > 3 ? 'high' : 'medium',
            recommendation: `Compléter les champs: ${issues?.join(', ')}`

          });
        }
      });

      communes?.forEach(commune => {
        const issues = [];
        
        if (!commune?.name?.trim()) issues?.push('name');
        if (!commune?.district?.trim()) issues?.push('district');
        if (!commune?.description?.trim()) issues?.push('description');
        if (!commune?.population) issues?.push('population');
        
        if (issues?.length > 0) {
          newIssues?.missingData?.push({
            id: commune?.id,
            name: commune?.name || 'Sans nom',
            missingFields: issues,
            type: 'missing_commune_data',
            severity: issues?.length > 2 ? 'high' : 'medium',
            recommendation: `Compléter les informations: ${issues?.join(', ')}`
          });
        }
      });

      // Enhanced duplicate detection
      const communeNames = {};
      const propertyTitles = {};
      
      communes?.forEach(commune => {
        const name = commune?.name?.toLowerCase()?.trim();
        if (name) {
          if (communeNames?.[name]) {
            newIssues?.duplicates?.push({
              type: 'duplicate_commune_name',
              name: commune?.name,
              ids: [communeNames?.[name]?.id, commune?.id],
              items: [communeNames?.[name], commune],
              severity: 'high',
              recommendation: 'Fusionner les communes ou renommer l\'une d\'entre elles'
            });
          } else {
            communeNames[name] = commune;
          }
        }
      });

      // Check for duplicate property titles in same commune
      properties?.forEach(property => {
        if (property?.title?.trim() && property?.commune_id) {
          const key = `${property?.title?.toLowerCase()?.trim()}-${property?.commune_id}`;
          if (propertyTitles?.[key]) {
            newIssues?.duplicates?.push({
              type: 'duplicate_property_title',
              title: property?.title,
              commune: property?.commune?.name,
              ids: [propertyTitles?.[key]?.id, property?.id],
              items: [propertyTitles?.[key], property],
              severity: 'medium',
              recommendation: 'Vérifier s\'il s\'agit de doublons ou modifier les titres'
            });
          } else {
            propertyTitles[key] = property;
          }
        }
      });

      // Enhanced pricing anomaly detection
      const prices = properties
        ?.filter(p => p?.price && p?.price > 0)
        ?.map(p => p?.price)
        ?.sort((a, b) => a - b);

      if (prices?.length > 0) {
        const q1 = prices?.[Math.floor(prices?.length * 0.25)];
        const q3 = prices?.[Math.floor(prices?.length * 0.75)];
        const iqr = q3 - q1;
        const lowerBound = q1 - (1.5 * iqr);
        const upperBound = q3 + (1.5 * iqr);
        const median = prices?.[Math.floor(prices?.length / 2)];
        
        properties?.forEach(property => {
          if (property?.price && property?.price > 0) {
            // Extremely high prices (outliers)
            if (property?.price > upperBound && property?.price > median * 5) {
              newIssues?.pricingAnomalies?.push({
                id: property?.id,
                title: property?.title,
                price: property?.price,
                median: median,
                type: 'extremely_high_price',
                severity: 'high',
                recommendation: `Prix ${Math.round((property?.price / median - 1) * 100)}% supérieur à la médiane - vérifier la saisie`
              });
            }
            
            // Unusually low prices
            if (property?.price < lowerBound && property?.price < 1000000 && property?.property_type !== 'land') {
              newIssues?.pricingAnomalies?.push({
                id: property?.id,
                title: property?.title,
                price: property?.price,
                type: 'unusually_low_price',
                severity: 'medium',
                recommendation: 'Prix inhabituellement bas - vérifier l\'exactitude'
              });
            }

            // Missing decimals (prices ending in many zeros)
            if (property?.price > 1000000 && property?.price % 1000000 === 0) {
              newIssues?.pricingAnomalies?.push({
                id: property?.id,
                title: property?.title,
                price: property?.price,
                type: 'rounded_price_suspicious',
                severity: 'low',
                recommendation: 'Prix arrondi - vérifier la précision'
              });
            }
          }
        });
      }

      // Enhanced invalid references check
      properties?.forEach(property => {
        if (property?.agent_id && !property?.agent) {
          newIssues?.invalidReferences?.push({
            id: property?.id,
            title: property?.title,
            reference: 'agent_id',
            value: property?.agent_id,
            type: 'missing_agent_reference',
            severity: 'high',
            recommendation: 'Assigner un agent valide ou supprimer la référence'
          });
        }
        
        if (property?.owner_id && !property?.owner) {
          newIssues?.invalidReferences?.push({
            id: property?.id,
            title: property?.title,
            reference: 'owner_id',
            value: property?.owner_id,
            type: 'missing_owner_reference',
            severity: 'high',
            recommendation: 'Assigner un propriétaire valide ou supprimer la référence'
          });
        }

        // Check for properties without agent assignment
        if (!property?.agent_id && property?.status === 'available') {
          newIssues?.invalidReferences?.push({
            id: property?.id,
            title: property?.title,
            reference: 'agent_id',
            value: null,
            type: 'missing_agent_assignment',
            severity: 'medium',
            recommendation: 'Assigner un agent pour la gestion de cette propriété'
          });
        }
      });

      setIssues(newIssues);
      
    } catch (error) {
      console.error('Error running integrity check:', error);
    } finally {
      setChecking(false);
    }
  };

  const getTotalIssues = () => {
    return Object.values(issues)?.reduce((total, issueArray) => total + (issueArray?.length || 0), 0);
  };

  const getHighPriorityIssues = () => {
    const allIssues = Object.values(issues)?.flat();
    return allIssues?.filter(issue => issue?.severity === 'high')?.length || 0;
  };

  const fixOrphanedProperties = async () => {
    setFixing(true);
    let fixed = 0;
    const results = [];
    
    try {
      for (const property of issues?.orphanedProperties || []) {
        // Set commune_id to null for orphaned properties
        const { error } = await propertyService?.updateProperty(property?.id, {
          commune_id: null
        });
        
        if (!error) {
          fixed++;
          results?.push(`✅ Propriété "${property?.title}" corrigée`);
        } else {
          results?.push(`❌ Erreur pour "${property?.title}": ${error}`);
        }
      }
      
      setFixedCount(prev => prev + fixed);
      setAutoFixResults(results);
      
      // Refresh data and rerun check
      await onRefresh();
      await runIntegrityCheck();
      
    } catch (error) {
      console.error('Error fixing orphaned properties:', error);
      setAutoFixResults(['❌ Erreur inattendue lors de la correction']);
    } finally {
      setFixing(false);
    }
  };

  const fixMissingAgentAssignments = async () => {
    setFixing(true);
    let fixed = 0;
    const results = [];
    
    try {
      // Get the first available agent
      const { data: agents } = await communeManagementService?.from('user_profiles')?.select('id, full_name')?.eq('role', 'agent')?.eq('is_active', true)?.limit(1);

      if (!agents?.length) {
        setAutoFixResults(['❌ Aucun agent disponible pour l\'assignation']);
        return;
      }

      const defaultAgent = agents?.[0];

      const missingAgentIssues = issues?.invalidReferences?.filter(
        issue => issue?.type === 'missing_agent_assignment'
      );

      for (const issue of missingAgentIssues) {
        const { error } = await propertyService?.updateProperty(issue?.id, {
          agent_id: defaultAgent?.id
        });
        
        if (!error) {
          fixed++;
          results?.push(`✅ Agent "${defaultAgent?.full_name}" assigné à "${issue?.title}"`);
        } else {
          results?.push(`❌ Erreur pour "${issue?.title}": ${error}`);
        }
      }
      
      setFixedCount(prev => prev + fixed);
      setAutoFixResults(results);
      
      // Refresh data and rerun check
      await onRefresh();
      await runIntegrityCheck();
      
    } catch (error) {
      console.error('Error fixing missing agent assignments:', error);
      setAutoFixResults(['❌ Erreur inattendue lors de l\'assignation des agents']);
    } finally {
      setFixing(false);
    }
  };

  const copyIssueReport = () => {
    const allIssues = Object.values(issues)?.flat();
    const report = `RAPPORT D'INTÉGRITÉ DES DONNÉES - ${new Date()?.toLocaleString('fr-FR')}

RÉSUMÉ:
- Total des problèmes: ${getTotalIssues()}
- Problèmes haute priorité: ${getHighPriorityIssues()}

DÉTAILS:
${allIssues?.map((issue, index) => `
${index + 1}. ${issue?.type}
   - Sévérité: ${issue?.severity}
   - Élément: ${issue?.title || issue?.name}
   - Recommandation: ${issue?.recommendation}
`)?.join('')}

RECOMMANDATIONS GÉNÉRALES:
• Corrigez les propriétés orphelines en premier
• Vérifiez manuellement les anomalies de prix
• Complétez les données manquantes dans les formulaires
• Résolvez les doublons en fusionnant ou supprimant
• Vérifiez les références d'agents et propriétaires
`;

    navigator.clipboard?.writeText(report);
  };

  const formatPrice = (price) => {
    return new Intl.NumberFormat('fr-FR', {
      style: 'currency',
      currency: 'XOF',
      minimumFractionDigits: 0
    })?.format(price || 0);
  };

  const getSeverityColor = (severity) => {
    switch (severity) {
      case 'high': return 'text-red-600 bg-red-100';
      case 'medium': return 'text-orange-600 bg-orange-100';
      case 'low': return 'text-yellow-600 bg-yellow-100';
      default: return 'text-gray-600 bg-gray-100';
    }
  };

  const IssueCard = ({ title, icon, items, color, onFix, fixable = false, onSecondaryFix = null, secondaryFixLabel = '' }) => (
    <div className={`border rounded-lg p-4 ${color}`}>
      <div className="flex items-center justify-between mb-3">
        <h4 className="font-medium flex items-center">
          {React.createElement(icon, { className: "w-5 h-5 mr-2" })}
          {title} ({items?.length || 0})
        </h4>
        <div className="flex space-x-2">
          {onSecondaryFix && items?.filter(item => item?.type?.includes('missing_agent'))?.length > 0 && (
            <Button
              onClick={onSecondaryFix}
              size="sm"
              disabled={fixing}
              variant="outline"
              className="flex items-center"
            >
              <Users className="w-4 h-4 mr-1" />
              {secondaryFixLabel}
            </Button>
          )}
          {fixable && items?.length > 0 && (
            <Button
              onClick={onFix}
              size="sm"
              disabled={fixing}
              className="flex items-center"
            >
              {fixing ? (
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-1"></div>
              ) : (
                <Wrench className="w-4 h-4 mr-1" />
              )}
              Corriger
            </Button>
          )}
        </div>
      </div>
      
      {items?.length > 0 && (
        <div className="max-h-40 overflow-y-auto space-y-2">
          {items?.slice(0, 5)?.map((item, index) => (
            <div key={index} className="text-sm bg-white bg-opacity-50 p-3 rounded border-l-4 border-gray-300">
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  {item?.type === 'invalid_commune_reference' && (
                    <p><strong>{item?.title}</strong> référence une commune invalide</p>
                  )}
                  {item?.type === 'missing_essential_data' && (
                    <p><strong>{item?.title}</strong> manque: {item?.missingFields?.join(', ')}</p>
                  )}
                  {item?.type === 'missing_commune_data' && (
                    <p><strong>{item?.name}</strong> manque: {item?.missingFields?.join(', ')}</p>
                  )}
                  {item?.type === 'duplicate_commune_name' && (
                    <p>Commune <strong>"{item?.name}"</strong> dupliquée</p>
                  )}
                  {item?.type === 'duplicate_property_title' && (
                    <p>Propriété <strong>"{item?.title}"</strong> dupliquée dans {item?.commune}</p>
                  )}
                  {item?.type === 'extremely_high_price' && (
                    <p><strong>{item?.title}</strong>: {formatPrice(item?.price)} (médiane: {formatPrice(item?.median)})</p>
                  )}
                  {item?.type === 'unusually_low_price' && (
                    <p><strong>{item?.title}</strong>: {formatPrice(item?.price)} (suspect)</p>
                  )}
                  {item?.type === 'rounded_price_suspicious' && (
                    <p><strong>{item?.title}</strong>: {formatPrice(item?.price)} (prix arrondi)</p>
                  )}
                  {item?.type?.includes('missing_') && item?.type?.includes('_reference') && (
                    <p><strong>{item?.title}</strong> référence {item?.reference} invalide</p>
                  )}
                  {item?.type === 'missing_agent_assignment' && (
                    <p><strong>{item?.title}</strong> n'a pas d'agent assigné</p>
                  )}
                  
                  {item?.recommendation && (
                    <p className="text-xs text-gray-600 mt-1">💡 {item?.recommendation}</p>
                  )}
                </div>
                
                {item?.severity && (
                  <span className={`px-2 py-1 text-xs font-medium rounded ${getSeverityColor(item?.severity)}`}>
                    {item?.severity}
                  </span>
                )}
              </div>
            </div>
          ))}
          {items?.length > 5 && (
            <button 
              onClick={() => {
                setSelectedIssue({ title, items });
                setShowDetails(true);
              }}
              className="text-sm text-blue-600 hover:text-blue-800 underline"
            >
              ... voir {items?.length - 5} autres problèmes
            </button>
          )}
        </div>
      )}
    </div>
  );

  return (
    <>
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
        <div className="bg-white rounded-lg shadow-lg max-w-6xl w-full max-h-[90vh] overflow-hidden">
          {/* Header */}
          <div className="flex items-center justify-between p-6 border-b">
            <h2 className="text-xl font-semibold flex items-center">
              <Shield className="w-6 h-6 mr-2 text-blue-600" />
              Vérification d'Intégrité des Données
            </h2>
            <div className="flex items-center space-x-3">
              <Button
                onClick={copyIssueReport}
                variant="outline"
                className="flex items-center"
              >
                <Copy className="w-4 h-4 mr-2" />
                Copier Rapport
              </Button>
              <Button
                onClick={runIntegrityCheck}
                variant="outline"
                disabled={checking}
                className="flex items-center"
              >
                {checking ? (
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-gray-600 mr-2"></div>
                ) : (
                  <RefreshCw className="w-4 h-4 mr-2" />
                )}
                Reverifier
              </Button>
              <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded">
                <X className="w-5 h-5" />
              </button>
            </div>
          </div>

          <div className="p-6 overflow-y-auto max-h-[calc(90vh-140px)]">
            {/* Summary */}
            <div className="mb-6">
              {checking ? (
                <div className="text-center py-8">
                  <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
                  <p className="text-gray-600">Vérification en cours...</p>
                </div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
                  <div className="p-4 bg-gray-50 rounded-lg">
                    <div className="flex items-center">
                      {getTotalIssues() === 0 ? (
                        <CheckCircle className="w-8 h-8 text-green-600 mr-3" />
                      ) : (
                        <AlertTriangle className="w-8 h-8 text-orange-600 mr-3" />
                      )}
                      <div>
                        <h3 className="font-medium text-2xl">
                          {getTotalIssues()}
                        </h3>
                        <p className="text-sm text-gray-600">Total des problèmes</p>
                      </div>
                    </div>
                  </div>
                  
                  <div className="p-4 bg-red-50 rounded-lg">
                    <div className="flex items-center">
                      <AlertTriangle className="w-8 h-8 text-red-600 mr-3" />
                      <div>
                        <h3 className="font-medium text-2xl text-red-900">
                          {getHighPriorityIssues()}
                        </h3>
                        <p className="text-sm text-red-700">Haute priorité</p>
                      </div>
                    </div>
                  </div>

                  <div className="p-4 bg-green-50 rounded-lg">
                    <div className="flex items-center">
                      <CheckCircle className="w-8 h-8 text-green-600 mr-3" />
                      <div>
                        <h3 className="font-medium text-2xl text-green-900">
                          {fixedCount}
                        </h3>
                        <p className="text-sm text-green-700">Problèmes corrigés</p>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Auto-fix Results */}
            {autoFixResults?.length > 0 && (
              <div className="mb-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
                <h4 className="font-medium text-blue-900 mb-2 flex items-center">
                  <FileText className="w-4 h-4 mr-2" />
                  Résultats de Correction Automatique
                </h4>
                <div className="text-sm text-blue-800 space-y-1 max-h-32 overflow-y-auto">
                  {autoFixResults?.map((result, index) => (
                    <div key={index}>{result}</div>
                  ))}
                </div>
              </div>
            )}

            {/* Issues */}
            {!checking && getTotalIssues() > 0 && (
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                
                <IssueCard
                  title="Propriétés Orphelines"
                  icon={AlertTriangle}
                  items={issues?.orphanedProperties}
                  color="border-red-200 bg-red-50"
                  onFix={fixOrphanedProperties}
                  fixable={true}
                />

                <IssueCard
                  title="Références Invalides"
                  icon={AlertTriangle}
                  items={issues?.invalidReferences}
                  color="border-orange-200 bg-orange-50"
                  onFix={() => {}}
                  onSecondaryFix={fixMissingAgentAssignments}
                  secondaryFixLabel="Assigner Agents"
                  fixable={false}
                />

                <IssueCard
                  title="Données Manquantes"
                  icon={AlertTriangle}
                  items={issues?.missingData}
                  color="border-yellow-200 bg-yellow-50"
                  onFix={() => {}}
                  fixable={false}
                />

                <IssueCard
                  title="Doublons"
                  icon={AlertTriangle}
                  items={issues?.duplicates}
                  color="border-purple-200 bg-purple-50"
                  onFix={() => {}}
                  fixable={false}
                />

                <IssueCard
                  title="Anomalies de Prix"
                  icon={AlertTriangle}
                  items={issues?.pricingAnomalies}
                  color="border-blue-200 bg-blue-50"
                  onFix={() => {}}
                  fixable={false}
                />
              </div>
            )}

            {/* Enhanced Recommendations */}
            {!checking && getTotalIssues() > 0 && (
              <div className="mt-8 p-6 bg-gradient-to-r from-blue-50 to-indigo-50 border border-blue-200 rounded-lg">
                <h4 className="font-semibold text-blue-900 mb-4 text-lg">📋 Plan d'Action Recommandé</h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <h5 className="font-medium text-blue-800 mb-2">🔥 Actions Prioritaires</h5>
                    <ul className="text-sm text-blue-800 space-y-1">
                      <li>• <strong>Corrigez les propriétés orphelines en premier</strong> (impact système)</li>
                      <li>• <strong>Vérifiez manuellement les anomalies de prix</strong> (impact client)</li>
                      <li>• <strong>Complétez les données manquantes</strong> dans les formulaires</li>
                    </ul>
                  </div>
                  <div>
                    <h5 className="font-medium text-blue-800 mb-2">⚡ Actions Secondaires</h5>
                    <ul className="text-sm text-blue-800 space-y-1">
                      <li>• <strong>Résolvez les doublons</strong> en fusionnant ou supprimant</li>
                      <li>• <strong>Vérifiez les références d'agents et propriétaires</strong></li>
                      <li>• <strong>Assignez des agents</strong> aux propriétés sans gestion</li>
                    </ul>
                  </div>
                </div>
                
                <div className="mt-4 p-3 bg-blue-100 rounded-lg">
                  <p className="text-sm text-blue-700">
                    <strong>💡 Conseil:</strong> Utilisez les boutons "Corriger" pour les fixes automatiques, 
                    puis traitez manuellement les problèmes complexes via les formulaires d'édition.
                  </p>
                </div>
              </div>
            )}

            {/* Success State */}
            {!checking && getTotalIssues() === 0 && (
              <div className="text-center py-12">
                <CheckCircle className="w-20 h-20 text-green-600 mx-auto mb-6" />
                <h3 className="text-2xl font-semibold text-green-900 mb-2">✨ Données Parfaitement Intègres</h3>
                <p className="text-green-700 mb-6">
                  Aucun problème de qualité détecté dans votre base de données. 
                  <br />Toutes les propriétés et communes sont correctement configurées.
                </p>
                <div className="flex justify-center space-x-4">
                  <div className="text-center p-4 bg-green-50 rounded-lg">
                    <h4 className="font-medium text-green-900">✅ Propriétés</h4>
                    <p className="text-sm text-green-700">{properties?.length || 0} vérifiées</p>
                  </div>
                  <div className="text-center p-4 bg-green-50 rounded-lg">
                    <h4 className="font-medium text-green-900">✅ Communes</h4>
                    <p className="text-sm text-green-700">{communes?.length || 0} vérifiées</p>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="flex justify-end p-6 border-t bg-gray-50">
            <Button onClick={onClose} variant="outline">
              Fermer
            </Button>
          </div>
        </div>
      </div>

      {/* Issue Details Modal */}
      {showDetails && selectedIssue && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-[60]">
          <div className="bg-white rounded-lg shadow-lg max-w-4xl w-full max-h-[80vh] overflow-hidden">
            <div className="flex items-center justify-between p-4 border-b">
              <h3 className="text-lg font-semibold">{selectedIssue?.title} - Détails Complets</h3>
              <button 
                onClick={() => setShowDetails(false)}
                className="p-2 hover:bg-gray-100 rounded"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="p-4 overflow-y-auto max-h-[calc(80vh-120px)]">
              <div className="space-y-3">
                {selectedIssue?.items?.map((item, index) => (
                  <div key={index} className="p-4 border rounded-lg bg-gray-50">
                    <div className="flex justify-between items-start mb-2">
                      <h4 className="font-medium">{item?.title || item?.name}</h4>
                      {item?.severity && (
                        <span className={`px-2 py-1 text-xs font-medium rounded ${getSeverityColor(item?.severity)}`}>
                          {item?.severity}
                        </span>
                      )}
                    </div>
                    {item?.recommendation && (
                      <p className="text-sm text-blue-600 mb-2">💡 {item?.recommendation}</p>
                    )}
                    <div className="text-xs text-gray-600">
                      ID: {item?.id} | Type: {item?.type}
                    </div>
                  </div>
                ))}
              </div>
            </div>
            <div className="flex justify-end p-4 border-t">
              <Button onClick={() => setShowDetails(false)}>Fermer</Button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}